import { Suspense, lazy, ComponentType, memo, useCallback } from 'react';
import { useLazySection } from '@/hooks/useLazySection';

interface LazySectionProps {
  // Factory function that returns the lazy component
  factory: () => Promise<{ default: ComponentType }>;
  // Minimum height to prevent layout shift
  minHeight?: string;
  // Root margin for intersection observer
  rootMargin?: string;
  // Unique key for the section
  sectionKey: string;
}

// Minimal skeleton placeholder
const SectionSkeleton = memo(({ minHeight }: { minHeight: string }) => (
  <div 
    className="flex items-center justify-center bg-background/50"
    style={{ minHeight }}
  >
    <div className="flex flex-col items-center gap-3">
      <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      <div className="h-1 w-16 bg-gradient-to-r from-transparent via-primary/20 to-transparent rounded-full animate-pulse" />
    </div>
  </div>
));

SectionSkeleton.displayName = 'SectionSkeleton';

// Cache for lazy components to prevent re-creation
const componentCache = new Map<string, ReturnType<typeof lazy>>();

/**
 * LazySection component that only loads when near the viewport.
 * Uses Intersection Observer for efficient loading.
 */
export const LazySection = memo(({ 
  factory, 
  minHeight = '400px',
  rootMargin = '300px',
  sectionKey
}: LazySectionProps) => {
  const { ref, shouldLoad } = useLazySection({ rootMargin });

  // Get or create the lazy component (cached to prevent re-creation)
  const getLazyComponent = useCallback(() => {
    if (!componentCache.has(sectionKey)) {
      componentCache.set(sectionKey, lazy(factory));
    }
    return componentCache.get(sectionKey)!;
  }, [factory, sectionKey]);

  const LazyComponent = getLazyComponent();

  return (
    <div ref={ref}>
      {shouldLoad ? (
        <Suspense fallback={<SectionSkeleton minHeight={minHeight} />}>
          <LazyComponent />
        </Suspense>
      ) : (
        <SectionSkeleton minHeight={minHeight} />
      )}
    </div>
  );
});

LazySection.displayName = 'LazySection';
